/* eslint-disable react/default-props-match-prop-types */
// noinspection SuspiciousTypeOfGuard

import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouterLink} from '@modifi/router'
import {InferPropTypes} from '@modifi/typescript-utils'
import useBasePathPrefix from './useBasePathPrefix'

const propTypes = {
  type: PropTypes.oneOf(['button', 'link', 'anchor']),
  children: PropTypes.node.isRequired,
  // link props
  to: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      state: PropTypes.object,
    }).isRequired,
  ]),
  scrollToTop: PropTypes.bool,
  isExternal: PropTypes.bool,
  targetBlank: PropTypes.bool,
  pattern: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  params: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  state: PropTypes.object,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,

  // button component props
  buttonComponent: PropTypes.elementType as PropTypes.Requireable<
    React.ComponentType<
      React.PropsWithChildren<
        {
          className?: string
          icon?: boolean
          transparent?: boolean
          big?: boolean
          block?: boolean
          'data-target': string
        } & Record<string, any>
      >
    >
  >,
  className: PropTypes.string,
  icon: PropTypes.bool,
  isTransparent: PropTypes.bool,
  big: PropTypes.bool,
  block: PropTypes.bool,
}

const defaultProps = {
  type: 'link',
  // link props
  to: null,
  scrollToTop: true,
  isExternal: false,
  targetBlank: false,
  pattern: null,
  params: null,
  state: null,
  disabled: false,
  onClick: undefined,
  // button component props
  buttonComponent: undefined,
  className: null,
  icon: false,
  isTransparent: false,
  big: false,
  block: false,
}

export type LinkProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const Link: React.FC<LinkProps> = ({
  type,
  to,
  pattern,
  buttonComponent: ButtonComponent,
  className,
  icon,
  isTransparent,
  big,
  block,
  children,
  isExternal,
  ...props
}) => {
  const prefixer = useBasePathPrefix()

  if (typeof to !== 'undefined' && to !== null) {
    // eslint-disable-next-line no-param-reassign
    to = prefixer(to)
  }

  switch (type) {
    case 'link':
    case 'anchor':
      return (
        <RouterLink {...props} to={to} pattern={pattern}>
          {children}
        </RouterLink>
      )
    case 'button': {
      if (typeof ButtonComponent === 'string' || typeof ButtonComponent === 'undefined') {
        return null
      }
      return (
        <RouterLink to={to} params={pattern} {...props}>
          <ButtonComponent
            className={className ?? undefined}
            icon={icon}
            big={big}
            block={block}
            data-target={(typeof to === 'string' ? to : to?.pathname) || pattern || 'unkown'}
            transparent={isTransparent}
          >
            {children}
          </ButtonComponent>
        </RouterLink>
      )
    }
    default:
      return <>Please specify a type from "button", "link", "anchor"</>
  }
}

Link.propTypes = propTypes
Link.defaultProps = defaultProps

export default React.memo(Link)
